/**
 * The feature-flag API, typed at the edge of this package.
 *
 * Hand-written rather than pulled from the generated OpenAPI types, for the
 * reason `feature-admin` gives: the generated types are the authority and CI
 * fails on drift, but reaching into `components["schemas"][...]` at every call
 * site makes the code unreadable.
 */

import type { ApiClient } from "@hardauto/core-api";

/**
 * Where a flag's current state came from.
 *
 * `default` means no decision has been recorded for this tenant — the shipped
 * value applies. That is NOT the same as "off", and the interface must not
 * render it as though it were: an administrator who cannot tell an undecided
 * flag from a deliberately disabled one cannot reason about either.
 */
export type FlagSource = "default" | "tenant";

export interface Flag {
  key: string;
  label: string;
  module: string;
  description: string;
  enabled: boolean;
  value: Record<string, unknown>;
  source: FlagSource;
}

export interface FlagList {
  flags: Flag[];
}

/** Every settings call, in one place. */
export class SettingsApi {
  readonly #client: ApiClient;

  constructor(client: ApiClient) {
    this.#client = client;
  }

  listFlags(): Promise<FlagList> {
    return this.#client.request<FlagList>("/api/v1/admin/flags");
  }

  /** Record a decision for this tenant. */
  setFlag(key: string, enabled: boolean): Promise<Flag> {
    return this.#client.request<Flag>(`/api/v1/admin/flags/${encodeURIComponent(key)}`, {
      method: "PUT",
      body: { enabled },
    });
  }

  /**
   * Forget the decision, so the shipped default applies again.
   *
   * Distinct from setting it to the default's current value: the default can
   * change in a later release, and a flag left "undecided" follows it while an
   * explicitly-set one does not.
   */
  clearFlag(key: string): Promise<void> {
    return this.#client.request<void>(`/api/v1/admin/flags/${encodeURIComponent(key)}`, {
      method: "DELETE",
    });
  }
}
